import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { Breakpoints } from '@settings';
import { useProductsCollection } from '@/queries/useProductsCollection';
import Slider, { Settings as SliderSettings } from 'react-slick';
import classNames from 'classnames';
import ProductCard from '@features/shop/components/product-card';
import ProductImageSliderBullets from '@components/tailwind/ProductImageSliderBullets';
import { sliderGoTo } from '@features/customization/utils';

const MAX_NUMBER_OF_CARDS = 3;
const SLIDER_OPTIONS: Partial<SliderSettings> = {
  slidesToShow: MAX_NUMBER_OF_CARDS,
  slidesToScroll: 1,
  arrows: false,
  variableWidth: true,
  adaptiveHeight: false,
  infinite: true,
  centerMode: false,
  dots: false,
  responsive: [
    {
      breakpoint: Breakpoints.MD,
      settings: {
        slidesToShow: 1.21,
      },
    },
  ],
};

const ProductSlider = ({ attributes: { title }, children }) => {
  const [activeSlide, setActiveSlide] = useState(0);
  const isMobile = useMediaQuery({ maxWidth: Breakpoints.MD });

  const { data, refetch: getCollection } = useProductsCollection({
    'attributes[]': children.map(({ attributes: { typeId, sizeId, colorId } }) => {
      return JSON.stringify({ typeId, sizeId, colorId });
    }),
  });

  useEffect(() => {
    getCollection();
  }, [children]);

  const sliderRef = useRef<Slider>(null);

  if (!data?.items || data.items.length === 0) return null;

  // TODO children don't necessarily map 1-to-1 with returned results
  const _childrenWithData = children.map((child) => {
    const item = data?.items?.find((i) => i.typeId === child.attributes.typeId);
    return {
      child,
      item,
    };
  });
  const childrenWithData = _childrenWithData.filter((i) => !!i.item);

  const numSlides = childrenWithData.length;
  const numPages = numSlides - MAX_NUMBER_OF_CARDS + 1;
  const renderCarousel = isMobile || numSlides > 3;

  if (!numSlides) return null;

  return (
    <div className='tw-my-5'>
      {title && (
        <h3 className='tw-mb-6 tw-text-center tw-text-2xl tw-font-bold tw-text-body md:tw-mb-10'>{title}</h3>
      )}

      <div
        className={classNames({
          'tw-bg-white tw-py-5': isMobile,
        })}>
        {!renderCarousel && (
          <div className='tw-mx-auto tw-flex tw-w-full tw-max-w-[1440px] tw-justify-center tw-gap-4'>
            {childrenWithData.map(({ child, item }, index) => (
              <ProductCard
                className='tw-max-w-[330px]'
                key={index}
                displayPosition={index + 1}
                item={item}
                features={child.attributes.features}
              />
            ))}
          </div>
        )}

        {renderCarousel && (
          <div className='tw-mx-auto tw-max-w-[1440px]'>
            <Slider ref={sliderRef} {...SLIDER_OPTIONS} beforeChange={(_, next) => setActiveSlide(next)}>
              {childrenWithData.map(({ child, item }, index) => (
                <div className='tw-px-1 tw-pb-5 xs:tw-px-2' key={index}>
                  <ProductCard
                    className='tw-min-h-[460px] tw-w-[280px] xs:tw-w-[330px]'
                    displayPosition={index + 1}
                    item={item}
                    features={child.attributes.features}
                  />
                </div>
              ))}
            </Slider>

            <ProductImageSliderBullets
              count={isMobile ? numSlides : numPages}
              onClick={sliderGoTo(sliderRef)}
              activeSlide={activeSlide}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductSlider;
